import React, { useState, useEffect, useRef } from 'react'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import CircularProgress from '@material-ui/core/CircularProgress'
import { makeStyles } from '@material-ui/core/styles'

import { CustomFormControlLabel } from 'components/CustomFormControlLabel'
import { CustomCheckbox } from 'components/CustomCheckbox'
import CustomTextField from 'components/CustomTextField'
import { useApiCall } from 'hooks'
import { searchPrizes } from 'features/Main/Prizes/api'
import { SearchPrize, SearchPrizesResponse } from 'features/Main/Prizes/types'

const useStyles = makeStyles(theme => ({
  container: {
    position: 'relative',
    width: '100%',
  },
  suggestionsContainer: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 1000,
    maxHeight: '200px',
    overflowY: 'auto',
    marginTop: 4,
  },
  suggestionItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))

interface RewardFilterProps {
  rewardId: string
  setRewardId: (id: string) => void
  invertedLogic: boolean
  setInvertedLogic: (value: boolean) => void
  disabled?: boolean
}

const RewardFilter: React.FC<RewardFilterProps> = ({
  rewardId,
  setRewardId,
  invertedLogic,
  setInvertedLogic,
  disabled = false,
}) => {
  const classes = useStyles()
  const [searchRewards, isSearching] = useApiCall<string, SearchPrizesResponse>(searchPrizes)
  const [options, setOptions] = useState<SearchPrize[]>([])
  const [inputValue, setInputValue] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout>()

  const handleInvertedLogicChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInvertedLogic(event.target.checked)
  }

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setOptions([])
      setShowSuggestions(false)
      return
    }

    try {
      const { prizes } = await searchRewards(query)
      setOptions(prizes)
      setShowSuggestions(prizes.length > 0)

      // Si es un ID numérico y hay match exacto, seleccionarlo automáticamente
      if (/^\d+$/.test(query.trim())) {
        const match = prizes.find(p => p.id.toString() === query.trim())
        if (match) {
          setRewardId(match.id.toString())
          setInputValue(`[${match.id}] - ${match.title}`)
          setShowSuggestions(false)
        }
      }
    } catch (err) {
      console.error(err)
      setOptions([])
      setShowSuggestions(false)
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setInputValue(value)

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    if (!value.trim()) {
      setRewardId('')
      setOptions([])
      setShowSuggestions(false)
      return
    }

    timeoutRef.current = setTimeout(() => {
      handleSearch(value)
    }, 300)
  }

  const handleSelectOption = (prize: SearchPrize) => {
    setRewardId(prize.id.toString())
    setInputValue(`[${prize.id}] - ${prize.title}`)
    setShowSuggestions(false)
    setOptions([])
  }

  useEffect(() => {
    if (rewardId && !inputValue) {
      handleSearch(rewardId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewardId])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <div className={classes.container}>
          <CustomTextField
            label="ID de Premio"
            placeholder="Buscar por ID o título"
            value={inputValue}
            onChange={handleInputChange}
            fullWidth
            disabled={disabled}
            InputProps={{
              endAdornment: isSearching ? <CircularProgress color="inherit" size={20} /> : null,
            }}
          />
          {showSuggestions && options.length > 0 && (
            <Paper className={classes.suggestionsContainer}>
              <List dense>
                {options.map(prize => (
                  <ListItem
                    key={prize.id}
                    className={classes.suggestionItem}
                    onClick={() => handleSelectOption(prize)}
                  >
                    <ListItemText primary={`[${prize.id}] - ${prize.title}`} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </div>
      </Grid>
      <Grid item xs={12} sm={6} alignContent="center">
        <CustomFormControlLabel
          control={
            <CustomCheckbox
              checked={invertedLogic}
              onChange={handleInvertedLogicChange}
              color="primary"
              disabled={disabled}
            />
          }
          label={
            invertedLogic ? 'Usuarios que utilizan el premio' : 'Usuarios que NO utilizan el premio'
          }
        />
      </Grid>
    </Grid>
  )
}

RewardFilter.defaultProps = {
  disabled: false,
}

export default RewardFilter
